import React, { useState, useEffect, useRef } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  PopoverOrigin,
} from '@mui/material';
import { CaretDown, Check } from '@phosphor-icons/react';
import classNames from 'classnames';

import { ButtonVariant, ButtonColour } from './getBtnClass';
import { Button, Spinner } from './';

type Item = {
  type?: string;
  label?: string;
  icon?: React.ReactNode;
  customContent?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  isProcessing?: boolean;
  isCurrent?: boolean;
  show?: boolean;
  closeOnClick?: boolean;
  className?: string;
  isSmall?: boolean;
};

type Props = {
  btnData: {
    label?: string;
    icon?: React.ReactNode;
    variant?: ButtonVariant;
    colour?: ButtonColour;
    withCaret?: boolean;
    className?: string;
  };
  menuHeading?: string;
  menuItems: Item[];
  position?: 'bottom-left' | 'bottom-right' | 'right' | 'left';
  onOpen?: () => void;
  onClose?: () => void;
};

const Dropdown = (props: Props) => {
  const {
    btnData,
    menuHeading,
    menuItems,
    position = 'bottom-left',
    onOpen,
    onClose,
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const isOpenDropdown = Boolean(anchorEl);

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    onClose && onClose();
  };

  const handleToggleDropdown = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();

    if (!isOpenDropdown) {
      setAnchorEl(event.currentTarget);
      onOpen && onOpen();
    } else handleCloseDropdown();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      !anchorEl?.contains(event.target as Node) &&
      !menuRef.current?.contains(event.target as Node)
    )
      handleCloseDropdown();
  };

  useEffect(() => {
    if (anchorEl) document.addEventListener('mousedown', handleClickOutside);
    else document.removeEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorEl]);

  const renderItemContent = (item: Item, index: number) => {
    if (item.type === 'divider')
      return <hr key={index} className="my-2 border-grey-200 opacity-100" />;
    else if (item.type === 'custom')
      return <div key={index}>{item.customContent}</div>;
    else
      return (
        <MenuItem
          key={index}
          onClick={() => {
            item.onClick && item.onClick();
            if (item.closeOnClick) handleCloseDropdown();
          }}
          disabled={item.disabled}
          className={classNames(item.className, 'transition-all', {
            'px-3': item.isSmall,
          })}
          selected={item.isCurrent}
          sx={{ '.MuiListItemIcon-root': { minWidth: 0 } }}
        >
          {item.customContent ?? (
            <>
              {item.icon && (
                <ListItemIcon
                  className={classNames({
                    'mr-4 text-[1.25rem]': !item.isSmall,
                    'mr-2 text-[1.1rem]': item.isSmall,
                  })}
                >
                  {item.isProcessing ? (
                    <Spinner size="xs" hoverColor="white" />
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
              )}

              <ListItemText>
                <div
                  className={classNames({
                    'text-sm': !item.isSmall,
                    'text-xs': item.isSmall,
                  })}
                >
                  {item.label}
                </div>
              </ListItemText>

              {item.isCurrent && (
                <Check weight="bold" className="ml-2 float-end" />
              )}
            </>
          )}
        </MenuItem>
      );
  };

  // bottom-left is default position
  let positionData: {
    transformOrigin: PopoverOrigin;
    anchorOrigin: PopoverOrigin;
  } = {
    transformOrigin: { horizontal: 'left', vertical: 'top' },
    anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
  };
  if (position === 'bottom-right')
    positionData = {
      transformOrigin: { horizontal: 'right', vertical: 'top' },
      anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
    };
  else if (position === 'right')
    positionData = {
      transformOrigin: { horizontal: 'left', vertical: 'top' },
      anchorOrigin: { horizontal: 'right', vertical: 'top' },
    };
  else if (position === 'left')
    positionData = {
      transformOrigin: { horizontal: 'right', vertical: 'top' },
      anchorOrigin: { horizontal: 'left', vertical: 'top' },
    };

  return (
    <React.Fragment>
      <Button
        onClick={handleToggleDropdown}
        variant={btnData.variant ?? 'primary-thin'}
        colour={btnData.colour ?? 'primary'}
        withGap={btnData.withCaret}
        className={btnData.className}
      >
        {btnData.icon}
        {btnData.label && <div>{btnData.label}</div>}
        {btnData.withCaret && <CaretDown weight="bold" />}
      </Button>

      <Menu
        ref={menuRef}
        anchorEl={anchorEl}
        open={isOpenDropdown}
        onClose={handleCloseDropdown}
        disableScrollLock
        transformOrigin={positionData.transformOrigin}
        anchorOrigin={positionData.anchorOrigin}
        className={classNames('pointer-events-none', {
          'mt-2': position?.includes('bottom'),
          'ml-2': position === 'right',
          'mr-2': position === 'left',
        })}
        sx={{
          '.MuiMenu-paper': { pointerEvents: 'auto' },
          '.MuiMenuItem-root': { padding: '0.5rem 1rem' },
          '.MuiList-root:has(.MuiMenuItem-root:hover) .MuiMenuItem-root:not(:hover)':
            { backgroundColor: 'transparent' },
          '.MuiMenuItem-root.Mui-selected': {
            backgroundColor: 'var(--light-grey-2)',
          },
          '.MuiMenuItem-root:hover': {
            backgroundColor: 'var(--light-grey-1) !important',
          },
          '.MuiListItemIcon-root': { color: 'black' },
        }}
      >
        {menuHeading && (
          <React.Fragment>
            <div className="px-4 text-sm font-bold">{menuHeading}</div>

            <hr className="my-2" />
          </React.Fragment>
        )}

        {menuItems.map(
          (item: Item, index: number) =>
            item.show !== false && renderItemContent(item, index),
        )}
      </Menu>
    </React.Fragment>
  );
};

export default Dropdown;
